"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight, Check, Clock } from "lucide-react";
import Link from "next/link";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];

export default function TimeClockHero() {
  const { t } = useLang();
  const [pin, setPin] = useState("");

  const pressKey = (k: string) => {
    if (k === "⌫") {
      setPin((p) => p.slice(0, -1));
      return;
    }
    if (k === "" || pin.length >= 4) return;
    const next = pin + k;
    setPin(next);
    if (next.length === 4) {
      setTimeout(() => setPin(""), 1800);
    }
  };

  const success = pin.length === 4;

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#FAFAF8]">
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(215,38,61,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("Time clock · Built into the POS", "Time clock · Incorporado al POS")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("PIN in.", "PIN in.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Get paid right.", "Cobra bien.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "Staff clock in with a 4-digit PIN at any iPad. Timesheets calculate themselves with overtime. Friday morning payroll prep: minutes, not hours.",
            "El staff entra con un PIN de 4 dígitos en cualquier iPad. Los timesheets se calculan solos con overtime. Preparar la nómina del viernes en la mañana: minutos, no horas."
          )}
        </motion.p>

        {/* Interactive PIN keypad */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white border border-black/5 rounded-[32px] p-8 md:p-12 max-w-[560px] mx-auto shadow-[0_30px_80px_rgba(0,0,0,0.06)] mb-12"
        >
          <div className="text-center mb-6">
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-2">
              {t("Clock-in PIN", "PIN clock-in")}
            </div>
            <div className="text-[14px] font-bold text-redy-charcoal mb-5">
              Maria L. · {t("Try it ↓", "Pruébalo ↓")}
            </div>

            {/* PIN dots */}
            <div className="flex items-center justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map((i) => {
                const filled = i < pin.length;
                return (
                  <motion.div
                    key={i}
                    animate={{
                      scale: filled && i === pin.length - 1 ? [1.3, 1] : 1,
                      backgroundColor: filled ? (success ? "#2ECC71" : "#F4722B") : "#F0EDE8",
                    }}
                    transition={{ duration: 0.25 }}
                    className="w-4 h-4 rounded-full"
                  />
                );
              })}
            </div>

            {/* Success message */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-center gap-2 text-[12px] font-black uppercase tracking-wider"
                style={{ color: "#2ECC71" }}
              >
                <Check className="w-3.5 h-3.5" strokeWidth={3} />
                {t("Clocked in at 11:32 AM", "Entrada 11:32 AM")}
              </motion.div>
            )}
          </div>

          {/* Keypad */}
          <div className="grid grid-cols-3 gap-3 max-w-[280px] mx-auto">
            {KEYS.map((k, i) => {
              if (k === "") return <div key={i} />;
              return (
                <button
                  key={k}
                  onClick={() => pressKey(k)}
                  className="aspect-square rounded-[20px] bg-[#FAFAF8] border border-black/5 text-redy-charcoal text-[22px] font-black hover:bg-black/5 active:scale-95 transition-all"
                >
                  {k}
                </button>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center"
        >
          <Link
            href="/signup"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 20px 60px rgba(244, 114, 43, 0.4)",
            }}
          >
            {t("Set up your team in minutes →", "Setup de tu equipo en minutos →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
